import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  CalendarDays,
  CalendarOff,
  CalendarRange,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  FileDown,
  ListChecks,
  SlidersHorizontal,
  Users,
  Wallet,
} from 'lucide-react';
import { erfuelltRecht, geldText } from '@symphenia/shared-types';
import { DashboardKopf, DashboardListe, Kachel, KachelRaster } from '@/components/dashboard';
import { formCard, pageWide } from '@/lib/form-styles';
import { useAuthStore } from '@/lib/auth.store';
import { fetchPayrollDashboard, type LohnDashboard, type LohnmonatLage, type Monatszahlen } from '../api/payroll.api';
import { HINWEIS_TEXT, LAGE_TEXT } from './PayrollMonthsPage';

/**
 * Dashboard der Lohnverrechnung (`/payroll/dashboard`, AP-40 7.6 und AP-22 Teil 2, J).
 *
 * Die Startseite des Bereichs: Für den gewählten Monat (Vorgabe: der Vormonat —
 * der, der gerade abgeschlossen wird) steht da, für wen die Erfassung vorliegt,
 * wer fertiggestellt ist und was auffällt. Jede Kachel führt in die
 * Monatsübersicht mit gesetztem Filter.
 *
 * **Die Zahlen des Monats kommen nur aus fertiggestellten Lohnmonaten** — ein
 * offener Monat ändert sich noch, eine Summe darüber wäre eine Zahl ohne Stand.
 *
 * Darunter bleibt der Wegweiser zu den Seiten des Bereichs; eine Kachel, deren
 * Seite man nicht öffnen darf, fehlt (MD-7).
 */

const AREAS = [
  { to: '/payroll/months', icon: ListChecks, title: 'Monatsübersicht', text: 'Stand je Mitarbeiter, alle vollständigen fertigstellen.', permission: 'payroll.month.read' },
  { to: '/payroll/entry', icon: CalendarRange, title: 'Monatserfassung', text: 'Tage erfassen, Abgabezettel hinterlegen, Monat fertigstellen.', permission: 'payroll.day_entry.read' },
  { to: '/payroll/export', icon: FileDown, title: 'Monatsauswertung', text: 'Fertiggestellte Monate als Datei ausgeben oder per E-Mail verschicken.', permission: 'payroll.day_entry.read' },
  { to: '/payroll/employees', icon: Users, title: 'Mitarbeiter', text: 'Wer abgerechnet wird — auch Mitarbeiter ohne eigenen Zugang.', permission: 'payroll.employee.read' },
  { to: '/payroll/work-schedules', icon: ClipboardList, title: 'Arbeitszeitmodelle', text: 'Wochenstunden und Tages-Soll je Mitarbeiter, mit Gültig-ab.', permission: 'payroll.work_schedule.read' },
  { to: '/payroll/holidays', icon: CalendarDays, title: 'Feiertage', text: 'Kalender des Mandanten, gesetzliche Feiertage je Jahr.', permission: 'payroll.holiday.read' },
  { to: '/payroll/absence-types', icon: CalendarOff, title: 'Fehlzeit-Arten', text: 'Urlaub, Krankenstand, Zeitausgleich — mit ihren Kennzeichen.', permission: 'payroll.absence_type.read' },
  { to: '/payroll/settings', icon: SlidersHorizontal, title: 'Einstellungen', text: 'Schwellen, Sätze, Zeitfenster und was mit fertigen Monaten geschieht.', permission: 'payroll.setting.read' },
];

const MONATSNAMEN = [
  'Jänner', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];
const KURZ = ['Jän', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

function zpad(n: number): string {
  return String(n).padStart(2, '0');
}

function verschiebe(monat: string, um: number): string {
  const [j, m] = monat.split('-').map(Number);
  const d = new Date(j, m - 1 + um, 1);
  return `${d.getFullYear()}-${zpad(d.getMonth() + 1)}`;
}

function std(n: number): string {
  return n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function datumZeit(iso: string): string {
  const d = new Date(iso);
  return `${zpad(d.getDate())}.${zpad(d.getMonth() + 1)}. ${zpad(d.getHours())}:${zpad(d.getMinutes())}`;
}

interface Offen {
  userId: string;
  name: string;
  lage: LohnmonatLage;
  tageErfasst: number;
  tageSoll: number;
  hinweise: string[];
}
interface Geaendert {
  id: string;
  userId: string;
  name: string;
  reason: string | null;
  at: string;
}
interface Saldo {
  userId: string;
  name: string;
  zaSaldo: number;
}

/** Eine Zeile „Zahlen des Monats": Wert, daneben der Vormonat. */
function Zahl({ label, wert, davor }: { label: string; wert: string; davor: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-border py-1 last:border-0">
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd className="text-right">
        <span className="text-sm font-medium tabular-nums text-foreground">{wert}</span>
        <span className="ml-2 text-xs tabular-nums text-muted-foreground">{davor}</span>
      </dd>
    </div>
  );
}

function fehlzeiten(z: Monatszahlen): string {
  const teile = Object.entries(z.fehlzeitTage).filter(([, tage]) => tage > 0);
  return teile.length ? teile.map(([k, tage]) => `${k} ${String(tage).replace('.', ',')}`).join(' · ') : '—';
}

export default function PayrollPage() {
  const rechte = useAuthStore((s) => s.user?.permissions);
  const darfStand = erfuelltRecht('payroll.month.read', rechte);
  const areas = AREAS.filter((a) => erfuelltRecht(a.permission, rechte));

  const [params, setParams] = useSearchParams();
  const gewaehlt = /^\d{4}-\d{2}$/.test(params.get('month') ?? '') ? params.get('month')! : undefined;
  const [daten, setDaten] = useState<LohnDashboard | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  const laden = useCallback(async () => {
    if (!darfStand) return;
    try {
      setDaten(await fetchPayrollDashboard(gewaehlt));
      setFehler(null);
    } catch {
      setFehler('Das Dashboard konnte nicht geladen werden.');
    }
  }, [darfStand, gewaehlt]);

  useEffect(() => {
    void laden();
  }, [laden]);

  const monat = daten?.month ?? gewaehlt;
  const setzeMonat = (neu: string) => {
    const p = new URLSearchParams(params);
    p.set('month', neu);
    setParams(p, { replace: true });
  };
  const [jahr, mon] = (monat ?? '2000-01').split('-').map(Number);
  const kachel = (key: string) => daten?.tiles?.[key];
  const z = daten?.zahlen;
  const mitarbeiter = kachel('employees')?.count ?? 0;
  const fertig = kachel('final')?.count ?? 0;
  const anteil = mitarbeiter ? Math.round((fertig / mitarbeiter) * 100) : 0;
  const zurErfassung = (userId: string) => `/payroll/entry?user=${userId}&month=${monat}`;

  return (
    <div className={pageWide}>
      <DashboardKopf
        // AP-18 15.3-7: Für den Lohn hat der Assistent keine Werkzeuge — also auch kein Feld, das sie verspricht.
        assistent={false}
        symbol={Wallet}
        titel="Lohnverrechnung"
        untertitel="Aufbereitung der Monatsdaten für die Übergabe an BMD."
        bereich="all"
        onBereich={() => undefined}
        umschalter={false}
        stand={daten?.stand}
        aktion={
          monat ? (
            <div className="flex items-center rounded-md border border-border">
              <button
                type="button"
                className="rounded-l-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                onClick={() => setzeMonat(verschiebe(monat, -1))}
                aria-label="Vormonat"
              >
                <ChevronLeft size={16} />
              </button>
              <span className="min-w-[8.5rem] text-center text-sm font-medium tabular-nums">
                {MONATSNAMEN[mon - 1]} {jahr}
              </span>
              <button
                type="button"
                className="rounded-r-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                onClick={() => setzeMonat(verschiebe(monat, 1))}
                aria-label="Folgemonat"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          ) : undefined
        }
      />

      {fehler && <div className="text-sm text-destructive">{fehler}</div>}

      {daten && (
        <>
          <KachelRaster>
            <Kachel titel="Mitarbeiter im Monat" kachel={kachel('employees')} />
            <Kachel titel="Erfassung fehlt" ton="warnung" kachel={kachel('empty')} hinweis="noch kein Tag erfasst" />
            <Kachel titel="In Arbeit" kachel={kachel('inProgress')} hinweis="erfasst, aber nicht vollständig" />
            <Kachel titel="Bereit zum Fertigstellen" kachel={kachel('ready')} hinweis="vollständig erfasst, noch offen" />
            <Kachel
              titel="Fertiggestellt"
              kachel={kachel('final')}
              wert={`${fertig} von ${mitarbeiter}`}
              hinweis={
                <span className="mt-1 block h-1.5 overflow-hidden rounded-full bg-muted" aria-label={`${anteil} %`}>
                  <span className="block h-full rounded-full bg-green-600" style={{ width: `${anteil}%` }} />
                </span>
              }
            />
            <Kachel titel="Nach dem Fertigstellen geändert" ton="warnung" kachel={kachel('changed')} />
            <Kachel titel="Bereit für BMD" kachel={kachel('toExport')} hinweis="fertiggestellt, noch nicht exportiert" />
            <Kachel titel="Neu exportieren" ton="warnung" kachel={kachel('reExport')} hinweis="nach dem BMD-Export geändert" />
            <Kachel titel="Ohne Arbeitszeitmodell" ton="gefahr" kachel={kachel('noSchedule')} hinweis="hier lässt sich nicht rechnen" />
            <Kachel titel="Abgabezettel fehlt" ton="warnung" kachel={kachel('slipMissing')} hinweis="Weg „Abgabezettel“ ohne Datei" />
            <Kachel titel="Wartet auf Monatsbericht" kachel={kachel('waiting')} hinweis="Zeiterfassung, noch nicht abgegeben" />
            <Kachel titel="Übernahme offen" ton="warnung" kachel={kachel('importOpen')} hinweis="abgegeben, nicht übernommen" />
          </KachelRaster>

          <div className="grid gap-4 lg:grid-cols-3">
            {z && (
              <div className={formCard}>
                <div className="mb-2 flex items-baseline justify-between gap-2">
                  <h2 className="text-sm font-medium">Zahlen des Monats</h2>
                  <span className="text-xs text-muted-foreground">Vormonat</span>
                </div>
                {z.aktuell.lohnmonate === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    Noch kein Monat fertiggestellt — die Zahlen kommen nur aus Fertiggestelltem.
                  </p>
                ) : (
                  <dl>
                    <Zahl label="Fertiggestellte Monate" wert={String(z.aktuell.lohnmonate)} davor={String(z.vormonat.lohnmonate)} />
                    <Zahl label="Normalstunden" wert={std(z.aktuell.normalStunden)} davor={std(z.vormonat.normalStunden)} />
                    <Zahl label="Mehrarbeit" wert={std(z.aktuell.mehrarbeit)} davor={std(z.vormonat.mehrarbeit)} />
                    <Zahl
                      label="Überstunden (gewichtet)"
                      wert={`${std(z.aktuell.ueberstunden)} (${std(z.aktuell.ueberstundenGewichtet)})`}
                      davor={std(z.vormonat.ueberstunden)}
                    />
                    <Zahl label="ZA-Saldo" wert={std(z.aktuell.zaSaldo)} davor={std(z.vormonat.zaSaldo)} />
                    <Zahl label="Fehlzeit-Tage" wert={fehlzeiten(z.aktuell)} davor={fehlzeiten(z.vormonat)} />
                    <Zahl label="Nächtigungen" wert={String(z.aktuell.naechtigungen)} davor={String(z.vormonat.naechtigungen)} />
                    <Zahl label="Montagezeit" wert={std(z.aktuell.montagezeit)} davor={std(z.vormonat.montagezeit)} />
                    <Zahl
                      label="Entfernungszulage"
                      wert={geldText(z.aktuell.entfernungszulageBetrag) || '—'}
                      davor={geldText(z.vormonat.entfernungszulageBetrag) || '—'}
                    />
                    <Zahl
                      label="Zulagen"
                      wert={geldText(z.aktuell.zulagenBetrag || null) || '—'}
                      davor={geldText(z.vormonat.zulagenBetrag || null) || '—'}
                    />
                  </dl>
                )}
              </div>
            )}

            <DashboardListe<Offen>
              titel="Noch offen"
              zeilen={daten.lists?.offen as Offen[] | undefined}
              schluessel={(o) => o.userId}
              alle={`/payroll/months?month=${monat}&lage.not=FINAL`}
              leer="Alle Monate sind fertiggestellt."
              zeile={(o) => (
                <>
                  <Link to={zurErfassung(o.userId)} className="min-w-0 flex-1 truncate hover:underline">
                    {o.name}
                  </Link>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {o.hinweise.length ? HINWEIS_TEXT[o.hinweise[0]] : LAGE_TEXT[o.lage]}
                    {o.tageSoll ? ` · ${o.tageErfasst}/${o.tageSoll}` : ''}
                  </span>
                </>
              )}
            />

            <div className="space-y-4">
              <DashboardListe<Geaendert>
                titel="Zuletzt geändert nach dem Fertigstellen"
                zeilen={daten.lists?.geaendert as Geaendert[] | undefined}
                schluessel={(g) => g.id}
                leer="Keine Änderungen."
                zeile={(g) => (
                  <>
                    <span className="w-20 shrink-0 text-xs tabular-nums text-muted-foreground">{datumZeit(g.at)}</span>
                    <Link to={zurErfassung(g.userId)} className="min-w-0 flex-1 truncate hover:underline" title={g.reason ?? undefined}>
                      {g.name}
                      {g.reason ? <span className="text-muted-foreground"> — {g.reason}</span> : null}
                    </Link>
                  </>
                )}
              />
              <DashboardListe<Saldo>
                titel="Höchste ZA-Salden"
                zeilen={daten.lists?.zaSalden as Saldo[] | undefined}
                schluessel={(s) => s.userId}
                leer="Noch kein Monat fertiggestellt."
                zeile={(s) => (
                  <>
                    <Link to={zurErfassung(s.userId)} className="min-w-0 flex-1 truncate hover:underline">
                      {s.name}
                    </Link>
                    <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{std(s.zaSaldo)} h</span>
                  </>
                )}
              />
            </div>
          </div>

          <div className={formCard}>
            <h2 className="mb-2 text-sm font-medium">Fertiggestellt im Jahr {jahr}</h2>
            <div className="grid grid-cols-6 gap-1.5 sm:grid-cols-12">
              {(daten.lists?.jahr as { monat: number; fertig: number }[] | undefined)?.map((m) => {
                const text = `${jahr}-${zpad(m.monat)}`;
                return (
                  <button
                    key={m.monat}
                    type="button"
                    onClick={() => setzeMonat(text)}
                    className={`rounded-md border px-1 py-1.5 text-center text-xs transition-colors hover:border-primary ${
                      text === monat ? 'border-primary bg-primary/10' : 'border-border'
                    }`}
                    title={`${MONATSNAMEN[m.monat - 1]}: ${m.fertig} fertiggestellt`}
                  >
                    <span className="block text-muted-foreground">{KURZ[m.monat - 1]}</span>
                    <span className={`block text-sm font-medium tabular-nums ${m.fertig ? 'text-foreground' : 'text-muted-foreground'}`}>
                      {m.fertig}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {areas.map(({ to, icon: Icon, title, text }) => (
          <Link
            key={to}
            to={to}
            className="rounded-lg border border-border bg-card p-3 shadow-sm transition-colors hover:border-primary/50 hover:bg-accent/30"
          >
            <div className="flex items-start gap-2.5">
              <Icon className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
              <div>
                <p className="text-sm font-medium text-foreground">{title}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">{text}</p>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
